import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Route, Redirect, Switch, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FmGalleryLayout, FmText, FmCenteredTabs, FmDropdownInput, FmIcon } from "../../../Components";
import { TextsUtils, shareableItemTypes } from "../../../Utils";
import { ANONYMOUS_ROUTES, LOGGED_IN_ROUTES, LANGUAGES_BY_KEYS } from "../../../Constants";
import { LoginForm, RegisterForm } from "./Components";
import AuthState from "../../../Redux/States/Auth";
import { GeneralState } from "../../../Redux";
import useStyles from "./LoginRegisterPage.style";
import { SavedItemsState } from "../../../Redux/States";
import { useTheme } from "../../../Hooks";
import { generalSlice } from "../../../Redux/States/General/GeneralSlice";
import { Languages } from "../../../Models";

const LoginRegisterPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [userIdToConfirm, setUserIdToConfirm] = useState<string | null>(null);
  const [tokenToConfirm, setTokenToConfirm] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [sharedItemId, setSharedItemId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState((location?.pathname || "") === ANONYMOUS_ROUTES.SIGN_IN ? 0 : 1);

  const { registerPageIsLoading, successfullyRegisteredForEmail, registrationError, registrationCompleted, emailResendInProgress } = useSelector(
    AuthState.Selectors.selectRegistrationStatus,
  );

  const { loginPageIsLoading, loginPageError, loginPageMailApproved, emailApprovementInProgress } = useSelector(
    AuthState.Selectors.selectLoginStatus,
  );

  dispatch(GeneralState.Actions.setUserIsNavigateToMainPage(false));
  // dispatch(GeneralState.Actions.setShowEditNavbar(false));
  dispatch(GeneralState.Actions.setGreenBar({ isShow: false, lastIsShow: false }));

  /* 
    Look for url update to set the right tab value (for example back button).
    Set approve email - related params from query params, in order to trigger effect on params change,
      and call 'approveEmail' thunk
   */
  useEffect(() => {
    (window as any).dataLayer.push({
      event: "LoginAndRegister",
      pageUrl: "https://multimatch.haifa-group.com/multi-match/auth/sign_in",
      pageTitle: "LoginAndRegister",
    });
  }, []);
  useEffect(() => {
    const isSignIn = (location?.pathname || "") === ANONYMOUS_ROUTES.SIGN_IN;
    setTabValue(isSignIn ? 0 : 1);
    if (isSignIn) {
      const params = new URLSearchParams(location.search);
      const userId = params.get("userId");
      const token = params.get("token");
      const saveItemId = params.get("saveItemId");
      const typeOfSharing = params.get("type");
      setUserIdToConfirm(userId);
      setTokenToConfirm(token);
      setSharedItemId(saveItemId);
      if (saveItemId) {
        dispatch(GeneralState.Actions.setUserNeedToViewSharableItem(false));
        dispatch(GeneralState.Actions.setSharedSavedItemId(Number(saveItemId)));
      }
    }
  }, [location]);

  const isRtl = useSelector(GeneralState.Selectors.selectIsRtl);
  const itemClass = isRtl ? classes.rtlItem : classes.item;
  const theme = useTheme();
  const [selectedLanguage, setLanguage] = useState("en");
  const confirmEmail = useCallback(() => {
    userIdToConfirm &&
      tokenToConfirm &&
      !emailApprovementInProgress &&
      !loginPageMailApproved &&
      dispatch(AuthState.Thunks.approveEmail(userIdToConfirm || "", tokenToConfirm || ""));
  }, [dispatch, userIdToConfirm, tokenToConfirm, emailApprovementInProgress, loginPageMailApproved]);

  const redirectToSharedProcess = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const saveItemId = params.get("saveItemId");
    const typeOfSharing = params.get("type");
    dispatch(GeneralState.Actions.setTypeOfSharableItem(shareableItemTypes.shareableItemTypes.empty));
    if (saveItemId) {
      dispatch(GeneralState.Actions.setUserNeedToViewSharableItem(true));
      dispatch(GeneralState.Actions.setTypeOfSharableItem(Number(typeOfSharing)));
    }
  }, []);

  useEffect(() => {
    if (!loginPageError) {
      confirmEmail();
      redirectToSharedProcess();
    }
  }, [confirmEmail, redirectToSharedProcess]);

  const onLoginSubmit = async (email: string, password: string, keepMeLoggedIn: boolean) => {
    await dispatch(AuthState.Thunks.login(email, password, keepMeLoggedIn, selectedLanguage));
  };

  const onRegisterFormSubmit = async (
    fullName: string,
    country: string,
    region: string | undefined,
    email: string,
    mainInterest: string,
    expertise: string,
    password: string,
    acceptTerms: boolean,
  ) => {
    await dispatch(AuthState.Thunks.register(fullName, email, country, region, expertise, mainInterest, password, acceptTerms));
    setRegisteredEmail(email);
  };

  const handleResendEmail = async () => {
    await dispatch(AuthState.Thunks.resendEmail(registeredEmail || ""));
  };

  const handleTabSwitch = (newValue: number) => {
    setTabValue(newValue);
    history.push(newValue === 0 ? ANONYMOUS_ROUTES.SIGN_IN : ANONYMOUS_ROUTES.SIGN_UP);
  };

  const handleForgotPassword = async (email: string) => {
    const res = await dispatch(AuthState.Thunks.forgotPassword(email || ""));

    return res;
  };
  const languageOptions = useMemo(() => {
    const languagesArray: { key: string; value: string }[] = [];

    for (const key in LANGUAGES_BY_KEYS) {
      languagesArray.push({ key: key, value: (LANGUAGES_BY_KEYS as any)[key] });
    }

    return languagesArray;
  }, []);

  const handleLanguageChange = async (newLanguage: string) => {
    setLanguage(newLanguage);
    await dispatch(generalSlice.actions.setLanguage(newLanguage));
    //dispatch(GeneralState.Thunks.setLanguage(newLanguage as any));
  };
  const activeLanguage = useSelector(GeneralState.Selectors.selectActiveLanguage);
  return (
    <div style={{ flex: 1 }}>
      <div style={{ position: "absolute", top: 20, left: 60 }}>
        <FmDropdownInput
          options={languageOptions}
          className={itemClass}
          onOptionSelect={handleLanguageChange}
          selectedOptionKey={activeLanguage}
          icon={<FmIcon url={theme.images.earth} />}
        />
      </div>

      <FmGalleryLayout>
        <div className={classes.container}>
          <div className={classes.content}>
            <div className={classes.titles_container}>
              <FmText variant={"bigTitle"}>{t("signIn.title")}</FmText>
              <span className={classes.textSmallWidth}>{t("signIn.description")}</span>
            </div>
            <div className={classes.tabsContainer}>
              <FmCenteredTabs
                tabValue={tabValue}
                setTabValue={handleTabSwitch}
                tabsNames={[t("authorizationTabs.signIn"), t("authorizationTabs.signUp")]}
              />
            </div>
            <Switch>
              <Route path={ANONYMOUS_ROUTES.SIGN_IN}>
                <LoginForm
                  onSubmit={onLoginSubmit}
                  loginInProgress={loginPageIsLoading}
                  error={loginPageError ? TextsUtils.getErrorMessage(t, loginPageError || "") : undefined}
                  mailApproved={loginPageMailApproved}
                  mailConfirmationInProgress={emailApprovementInProgress}
                  onForgotPasswordClicked={handleForgotPassword}
                />
              </Route>
              <Route path={ANONYMOUS_ROUTES.SIGN_UP}>
                <RegisterForm
                  onSubmit={onRegisterFormSubmit}
                  submitInProgress={registerPageIsLoading}
                  emailResendInProgress={emailResendInProgress}
                  submitted={registrationCompleted}
                  submittedEmail={successfullyRegisteredForEmail}
                  resendEmail={handleResendEmail}
                  error={registrationError ? TextsUtils.getErrorMessage(t, registrationError || "") : undefined}
                />
              </Route>
              <Redirect from="/" to={ANONYMOUS_ROUTES.SIGN_IN} />
            </Switch>
          </div>
        </div>
      </FmGalleryLayout>
    </div>
  );
};

export default LoginRegisterPage;
